/*
 * StackScore
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { isNil } from 'ramda';

import { DataValue, PercentageSticker } from '~/components';
import { getFlaskColor } from '~/helpers';

export type StackScoreProps = {
  sign?: string,
  title?: string,
  value?: number,
  valuePtc?: number,
  variant?: string,
  isSmall?: boolean,
  isReversed?: boolean,
  isVertical?: boolean,
  isVisiblePtc?: boolean,
  device?: $PropertyType<DeviceQuery, 'device'>,
  tooltip?: React$Node,
};

const SIZES = {
  SMALL: 47,
  ORDINARY: 50,
};

const StackScoreWrapper = styled.div.attrs(() => ({
  className: 'react-stack-score-wrapper',
}))`
  position: relative;
  z-index: 2;
  display: flex;
  width: 100%;
  align-items: center;

  ${ifProp(
    'isVertical',
    css`
      flex-direction: column-reverse;
      align-items: center;
      height: 98px;
      justify-content: flex-start;

      .react-stack-score-value {
        display: flex;
        flex: none;
        justify-content: center;
        margin-top: ${ifProp({ device: 'mobile' }, '4px', '8px')};
        width: 100%;
      }
    `,
    css`
      height: ${ifProp('isSmall', `${SIZES.SMALL}px`, `${SIZES.ORDINARY}px`)};
      justify-content: ${ifProp('isVisiblePtc', 'space-between', 'center')};
    `,
  )}
`;

const StackScoreValue = styled.div.attrs(() => ({
  className: 'react-stack-score-value',
}))`
  display: flex;
  color: ${getFlaskColor};
  flex: 1;
  font-size: 16px;
  font-weight: bold;

  ${ifProp(
    'isTitled',
    css`
      height: 100%;
      flex-direction: column;
      justify-content: flex-start;
    `,
    css`
      justify-content: center;
    `,
  )}

  ${ifProp(
    'isVisiblePtc',
    css`
      justify-content: ${ifProp('isVertical', 'center', 'normal')};
    `,
    css`
      justify-content: center;
    `,
  )}

  span {
    display: block;
  }
`;

const StackScoreTitle = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
`;

/**
 * StackScore React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {number} [props.sign] - percentage sticker sign
 * @param {number} [props.value] - value
 * @param {number} [props.valuePtc] - percentage
 * @param {string} [props.variant] - colors variant
 * @param {string} [props.title] - title
 * @param {boolean} [props.isSmall] - small size flag
 * @param {boolean} [props.isReversed] - score reversing flag
 * @param {boolean} [props.isVertical] - vertical alignment flag
 * @param {boolean} [props.isVisiblePtc] - percentage visibility flag
 * @param {React$Node} [props.tooltip] - tooltip
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @param {$PropertyType<DeviceQuery, 'orientation'>} [props.orientation] - display orientation
 * @example <caption>Simple usage</caption>
 * <StackScore value={3405} />
 * @returns {React.Node<*>} React-component
 */
export const StackScore = ({
  sign,
  title,
  value,
  valuePtc,
  variant,
  isSmall,
  isReversed,
  isVertical,
  isVisiblePtc,
  device,
  tooltip,
}: StackScoreProps) => {
  const isTitled = !!title;

  const showPtc = isVisiblePtc && !isNil(valuePtc);

  return (
    <StackScoreWrapper
      device={device}
      isSmall={isSmall}
      isVertical={isVertical}
      isVisiblePtc={showPtc}
      testid="stack-score"
    >
      <StackScoreValue
        variant={variant}
        isTitled={!!title}
        isReversed={!isReversed}
        isVisiblePtc={showPtc}
      >
        {isTitled && <StackScoreTitle>{title}</StackScoreTitle>}
        <DataValue value={value} isEmptyVisible />
      </StackScoreValue>
      {showPtc && (
        <PercentageSticker
          sign={sign}
          size={isSmall ? SIZES.SMALL : SIZES.ORDINARY}
          value={valuePtc}
          variant={variant}
          isReversed={isReversed}
          tooltip={tooltip}
        />
      )}
    </StackScoreWrapper>
  );
};

StackScore.defaultProps = {
  value: 0,
  valuePtc: 0,
  variant: 'one',
};
